import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
    Button, Table, Tag, Card, Space, Typography, App, Input, Dropdown, Menu,
    Tabs, Progress, InputNumber, Modal
} from 'antd';
import {
    PlusOutlined, ReloadOutlined, SearchOutlined, MoreOutlined,
    EyeOutlined, EditOutlined, UploadOutlined, SwapOutlined, DeleteOutlined
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';
import tenantService from '../../services/tenantService';
import TenantCreateModalWithTiers from './TenantCreateModalWithTiers';
import TenantDetailsDrawer from './TenantDetailsDrawer';
import logger from '../../utils/logger';
import {
    authenticatedGet,
    authenticatedPost,
    authenticatedPut,
    authenticatedDelete,
    buildApiUrl,
    API_ENDPOINTS,
} from '../../utils/authUtils';
import { getApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;
const { Search } = Input;

const TenantManagement = () => {
    const { message } = App.useApp();
    const { currentUser, impersonate } = useContext(AuthContext);

    // State management
    const [tenants, setTenants] = useState([]);
    const [tokenUsage, setTokenUsage] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState('list');
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [impersonating, setImpersonating] = useState(false);

    // Status badge color helper
    const getStatusColor = (status) => {
        switch (status) {
            case 'CREATING':
            case 'SETTING_UP':
            case 'PROVISIONING':
                return 'processing';
            case 'RDS_UPGRADING':
                return 'processing';
            case 'READY':
                return 'success';
            case 'FAILED':
            case 'PROVISIONING_FAILED':
                return 'error';
            default:
                return 'default';
        }
    };

    // Status display text
    const getStatusText = (status) => {
        switch (status) {
            case 'CREATING':
                return 'Creating AWS Account';
            case 'SETTING_UP':
                return 'Setting Up Resources';
            case 'PROVISIONING':
                return 'Provisioning Infrastructure';
            case 'RDS_UPGRADING':
                return 'Upgrading RDS';
            case 'READY':
                return 'Ready';
            case 'FAILED':
                return 'Failed';
            case 'PROVISIONING_FAILED':
                return 'Provisioning Failed';
            default:
                return status || 'Unknown';
        }
    };

    // Load tenant list
    const loadTenants = useCallback(async () => {
        try {
            const tenantList = await tenantService.getTenantList();
            setTenants(tenantList);
        } catch (err) {
            message.error(err.message || 'Failed to load tenants');
            logger.error('Error loading tenants:', err);
        } finally {
            setLoading(false);
        }
    }, [message]);

    // Load token usage
    const loadTokenUsage = useCallback(async () => {
        try {
            const usageData = await authenticatedGet(
                buildApiUrl(API_ENDPOINTS.TOKEN_USAGE.ALL)
            );
            setTokenUsage(usageData.tenants || []);
        } catch (err) {
            logger.error('Error loading token usage:', err);
        }
    }, []);

    // Load data and set up auto-refresh
    useEffect(() => {
        loadTenants();
        loadTokenUsage();

        // Set up auto-refresh
        const interval = setInterval(() => {
            loadTenants();
            loadTokenUsage();
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [loadTenants, loadTokenUsage]);

    // Handle successful tenant creation
    const handleTenantCreated = async () => {
        setShowCreateModal(false);
        await loadTenants();
        await loadTokenUsage();
    };

    // Handle tenant actions
    const handleViewDetails = (tenant) => {
        // Find matching token usage data
        const stats = tokenUsage.find(t => t.tenant_id === tenant.tenant_id || t.tenant_id === tenant.id);
        setSelectedTenant({ ...tenant, stats });
        setDrawerOpen(true);
    };

    const handleEdit = (tenant) => {
        message.info('Edit functionality coming soon');
        // TODO: Implement edit modal
    };

    const handleUpgrade = (tenant) => {
        message.info('Upgrade functionality coming soon');
        // TODO: Implement upgrade modal
    };

    const handleImpersonate = async (tenant) => {
        try {
            setImpersonating(true);
            await impersonate(tenant.tenant_id);
            message.success(`Now viewing as ${tenant.name}`);
            window.location.reload();
        } catch (error) {
            message.error(error.message || 'Failed to impersonate tenant');
            setImpersonating(false);
        }
    };

    const handleDelete = (tenant) => {
        Modal.confirm({
            title: 'Delete Tenant',
            content: (
                <div>
                    <p>Are you sure you want to delete <strong>{tenant.name}</strong>?</p>
                    <p style={{ color: 'red' }}>
                        <strong>WARNING:</strong> This action is <strong>irreversible</strong> and will:
                    </p>
                    <ul>
                        <li>Drop the entire tenant schema and all data</li>
                        <li>Delete all users, projects, documents, and conversations</li>
                        <li>Remove all archive files and embeddings</li>
                    </ul>
                </div>
            ),
            okText: 'Delete Permanently',
            okType: 'danger',
            onOk: async () => {
                try {
                    await authenticatedDelete(
                        buildApiUrl(`${API_ENDPOINTS.ADMIN.TENANTS}/${tenant.tenant_id}`)
                    );
                    message.success(`Tenant "${tenant.name}" deleted successfully`);
                    await loadTenants(); // Reload the tenant list
                } catch (error) {
                    logger.error('Error deleting tenant:', error);
                    message.error(getApiErrorMessage(error, 'Failed to delete tenant.'));
                }
            },
        });
    };

    // Update token limit
    const updateTokenLimit = async (tenantId, newLimit) => {
        try {
            await authenticatedPut(
                buildApiUrl(`${API_ENDPOINTS.TOKEN_USAGE.LIMIT}/${tenantId}`),
                { new_limit_millions: parseInt(newLimit) }
            );
            message.success('Token limit updated successfully!');
            await loadTokenUsage();
        } catch (err) {
            logger.error('Failed to update token limit:', err);
            message.error(getApiErrorMessage(err, 'Failed to update token limit.'));
        }
    };

    // Refresh token usage data
    const refreshTokenUsage = async () => {
        try {
            await authenticatedPost(
                buildApiUrl(API_ENDPOINTS.TOKEN_USAGE.REFRESH),
                {}
            );
            await loadTokenUsage();
            message.success('Usage data refreshed successfully!');
        } catch (err) {
            logger.error('Failed to refresh usage data:', err);
            message.error(getApiErrorMessage(err, 'Failed to refresh usage data.'));
        }
    };

    // Filter tenants based on search
    const filteredTenants = tenants.filter(tenant => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            tenant.name?.toLowerCase().includes(search) ||
            tenant.tenant_id?.toLowerCase().includes(search) ||
            tenant.id?.toLowerCase().includes(search) ||
            tenant.email?.toLowerCase().includes(search) ||
            tenant.tier?.toLowerCase().includes(search) ||
            tenant.status?.toLowerCase().includes(search)
        );
    });

    // Format date display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Get tenant token usage percentage
    const getTenantUsagePercentage = (tenant) => {
        const stats = tokenUsage.find(t => t.tenant_id === tenant.tenant_id || t.tenant_id === tenant.id);
        return stats?.usage?.usage_percentage || 0;
    };

    // Actions menu for each tenant
    const getActionsMenu = (tenant) => (
        <Menu
            items={[
                {
                    key: 'details',
                    label: 'Details',
                    icon: <EyeOutlined />,
                    onClick: () => handleViewDetails(tenant),
                },
                {
                    key: 'edit',
                    label: 'Edit',
                    icon: <EditOutlined />,
                    onClick: () => handleEdit(tenant),
                },
                {
                    key: 'upgrade',
                    label: 'Upgrade',
                    icon: <UploadOutlined />,
                    onClick: () => handleUpgrade(tenant),
                },
                {
                    key: 'impersonate',
                    label: 'Impersonate',
                    icon: <SwapOutlined />,
                    onClick: () => handleImpersonate(tenant),
                },
                {
                    type: 'divider',
                },
                {
                    key: 'delete',
                    label: 'Delete',
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: () => handleDelete(tenant),
                },
            ]}
        />
    );

    // Tenant List Table Columns
    const tenantListColumns = [
        {
            title: 'Tenant',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name, record) => (
                <div>
                    <div><Text strong>{name}</Text></div>
                    <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.email || 'No email'}</Text></div>
                </div>
            ),
        },
        {
            title: 'Class',
            dataIndex: 'tier',
            key: 'tier',
            width: 120,
            render: (tier) => {
                const isTraining = tier === 'training';
                return (
                    <Tag color={isTraining ? 'blue' : 'purple'}>
                        {isTraining ? 'Training' : 'Client'}
                    </Tag>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Infrastructure',
            key: 'infrastructure',
            width: 180,
            render: (_, record) => {
                const hasAWS = record.aws_account_id && record.aws_account_id.trim() !== '';

                if (!hasAWS) {
                    return (
                        <div>
                            <div><Tag color="green">Local</Tag></div>
                            <div><Text type="secondary" style={{ fontSize: '12px' }}>Shared infrastructure</Text></div>
                        </div>
                    );
                }

                return (
                    <div>
                        <div>{record.aws_account_id}</div>
                        <div><Text type="secondary" style={{ fontSize: '12px' }}>{record.aws_account_email || 'N/A'}</Text></div>
                    </div>
                );
            },
        },
        {
            title: 'Token Usage',
            key: 'token_usage',
            width: 180,
            render: (_, record) => {
                const percentage = getTenantUsagePercentage(record);
                const status = percentage > 75 ? 'exception' : percentage > 50 ? 'normal' : 'success';

                return (
                    <div>
                        <div style={{ marginBottom: 4 }}>
                            <Text strong>{percentage.toFixed(0)}%</Text>
                        </div>
                        <Progress
                            percent={Math.min(percentage, 100)}
                            status={status}
                            size="small"
                            showInfo={false}
                        />
                    </div>
                );
            },
            sorter: (a, b) => getTenantUsagePercentage(a) - getTenantUsagePercentage(b),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <Dropdown overlay={getActionsMenu(record)} trigger={['click']}>
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Dropdown>
            ),
        },
    ];

    // Token Usage Table Columns
    const tokenUsageColumns = [
        {
            title: 'Tenant Name',
            dataIndex: 'tenant_name',
            key: 'tenant_name',
            sorter: (a, b) => (a.tenant_name || '').localeCompare(b.tenant_name || ''),
        },
        {
            title: 'Total Tokens Used',
            key: 'total_tokens',
            render: (_, record) => {
                const tokens = record.usage?.total_tokens_used || 0;
                return <span>{(tokens / 1000000).toFixed(1)}M</span>;
            },
            sorter: (a, b) => (a.usage?.total_tokens_used || 0) - (b.usage?.total_tokens_used || 0),
        },
        {
            title: 'Token Limit',
            key: 'token_limit',
            render: (_, record) => {
                const limit = record.usage?.token_limit || 20000000;
                return (
                    <InputNumber
                        min={1}
                        max={1000}
                        defaultValue={limit / 1000000}
                        addonAfter="M"
                        onBlur={(e) => {
                            const newLimit = e.target.value;
                            if (newLimit !== (limit / 1000000).toString()) {
                                updateTokenLimit(record.tenant_id, newLimit);
                            }
                        }}
                    />
                );
            },
        },
        {
            title: 'Usage',
            key: 'usage',
            render: (_, record) => {
                const percentage = record.usage?.usage_percentage || 0;
                const status = percentage > 75 ? 'exception' : percentage > 50 ? 'normal' : 'success';
                return (
                    <div style={{ width: 200 }}>
                        <Progress
                            percent={Math.min(percentage, 100)}
                            status={status}
                            size="small"
                        />
                    </div>
                );
            },
            sorter: (a, b) => (a.usage?.usage_percentage || 0) - (b.usage?.usage_percentage || 0),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const percentage = record.usage?.usage_percentage || 0;
                if (percentage > 90) {
                    return <Tag color="red">Critical</Tag>;
                } else if (percentage > 75) {
                    return <Tag color="orange">Warning</Tag>;
                }
                return <Tag color="green">Good</Tag>;
            },
        },
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Header with Create Button */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24
            }}>
                <div>
                    <h2 style={{ margin: 0 }}>Tenant Management</h2>
                    <Text type="secondary">Manage tenants, billing, and usage</Text>
                </div>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            loadTenants();
                            loadTokenUsage();
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                    >
                        Create Tenant
                    </Button>
                </Space>
            </div>

            {/* Search Bar */}
            <Card style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Search tenants by name, ID, email, class, or status..."
                    allowClear
                    size="large"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: '100%' }}
                />
            </Card>

            {/* Tabs: Tenant List and Usage Breakdown */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'list',
                            label: `Tenant List (${filteredTenants.length})`,
                            children: (
                                <div>
                                    <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            Auto-refreshing every 30 seconds
                                        </Text>
                                    </div>
                                    <Table
                                        columns={tenantListColumns}
                                        dataSource={filteredTenants}
                                        loading={loading}
                                        rowKey="_id"
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} tenants`,
                                        }}
                                        onRow={(record) => ({
                                            onClick: () => handleViewDetails(record),
                                            style: { cursor: 'pointer' },
                                        })}
                                    />
                                </div>
                            ),
                        },
                        {
                            key: 'usage',
                            label: `Usage Breakdown (${tokenUsage.length})`,
                            children: (
                                <div>
                                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            Auto-refreshing every 30 seconds
                                        </Text>
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={refreshTokenUsage}
                                            loading={loading}
                                            size="small"
                                        >
                                            Refresh Usage Data
                                        </Button>
                                    </div>
                                    <Table
                                        columns={tokenUsageColumns}
                                        dataSource={tokenUsage}
                                        rowKey="tenant_id"
                                        loading={loading}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} tenants`,
                                        }}
                                    />
                                </div>
                            ),
                        },
                    ]}
                />
            </Card>

            {/* Create Tenant Modal */}
            <TenantCreateModalWithTiers
                visible={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                onSuccess={handleTenantCreated}
            />

            {/* Tenant Details Drawer */}
            <TenantDetailsDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                tenant={selectedTenant}
                tenantStats={selectedTenant?.stats?.usage}
            />
        </div>
    );
};

export default TenantManagement;
